<script lang="ts" setup>
import { IonText, modalController } from "@ionic/vue";
import { computed } from "vue";
import { useI18n } from "@/composables/useI18n";
import DateTimePickerModal from "@/components/modals/DateTimePickerModal.vue";
import { format } from "date-fns";
import { parseDateStringWithTimezoneToUTCString } from "@/utils/dates";
import { toastController } from "@ionic/vue";
import { getDateFnsLocales } from "@/i18n";
import { useFabriqStore } from "@/store/fabriq";
import FontIcon from "@/components/ui/FontIcon.vue";

type Props = {
  readonly?: boolean;
  date: string;
  canBeFuture?: boolean;
  siteTimezone?: string;
  errorMessage?: string;
};

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  date: "",
  canBeFuture: false,
});

const phoneTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
const eventTimezone = computed(() => {
  return props.siteTimezone || phoneTimezone;
});
const eventTimezoneDate = computed(() => {
  const nowDate = new Intl.DateTimeFormat("en-US", {
    timeZone: eventTimezone.value,
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date());
  return format(new Date(nowDate), "yyyy-MM-dd HH:mm", {
    locale: getDateFnsLocales(fabriqStore.locale),
  }) as string;
});

const emit = defineEmits(["modal", "date"]);
const { t } = useI18n();
const fabriqStore = useFabriqStore();
const formated = computed(() => {
  if (!props.date || !props.date.length) {
    return {
      date: t("datetime.emptyDueDate"),
      time: t("datetime.emptyDueDate"),
    };
  }

  const startDate = format(new Date(props.date), "yyyy-MM-dd", {
    locale: getDateFnsLocales(fabriqStore.locale),
  }) as string;

  const startTime = format(new Date(props.date), "HH:mm") as string;
  return { date: startDate, time: startTime };
});

async function showEventTimezone() {
  const toast = await toastController.create({
    message: `${eventTimezone.value}: ${eventTimezoneDate.value}`,
    duration: 2000,
  });
  toast.present();
}

const selectDate = async () => {
  if (props.readonly) return;
  let currentToast: any = null;
  const modal = await modalController.create({
    component: DateTimePickerModal,
    canDismiss: true,
    mode: "ios",
    breakpoints: [0, 1],
    initialBreakpoint: 1,
    componentProps: {
      date: props.date,
      noFutureDate: !props.canBeFuture,
      canClearDate: false,
      onDone: async ({ date, time }: { date: string; time: string }) => {
        const now = new Date();
        const dateString = `${date}T${time}`;
        const fullDate = new Date(
          props.siteTimezone
            ? parseDateStringWithTimezoneToUTCString(
                dateString,
                props.siteTimezone
              )
            : dateString
        );

        if (fullDate > now && !props.canBeFuture) {
          if (!currentToast) {
            const toast = await toastController.create({
              message: `🚨 ${t("eventContent.futureDateTime")}`,
              duration: 2000,
            });
            currentToast = toast;
            toast.present();
            toast.onDidDismiss().then(() => {
              currentToast = null;
            });
          }
          return;
        }
        modal.dismiss();

        emit("date", dateString);
      },
      onCancel: () => {
        modal.dismiss();
      },
    },
  });
  return modal.present();
};
</script>

<template>
  <div class="due-date-container">
    <font-icon
      v-if="eventTimezone !== phoneTimezone"
      name="info-circle"
      class="timezone-help-icon"
      size="1"
      color="var(--ion-color-primary-tint)"
      @click.stop.prevent="showEventTimezone"
    />
    <ion-text
      class="due-date"
      :class="{ 'due-date--error': errorMessage }"
      @click.stop.prevent="selectDate"
    >
      <span> {{ formated.date }} </span>
      <span> {{ formated.time }} </span>
    </ion-text>
    <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
  </div>
</template>

<style scoped>
.due-date-container {
  margin-top: calc(var(--ion-padding) / 4);
  display: flex;
  align-items: center;
  gap: calc(var(--ion-padding) / 2);
}

.due-date {
  display: inline-flex;
  align-items: center;
  padding: calc(var(--ion-padding) / 2);
  font-size: var(--font-size-m);
  gap: 0.5em;
}

.due-date--error {
  border: var(--ion-color-danger) solid 1px;
  border-radius: var(--f-border-radius);
}

.error-message {
  color: var(--ion-color-danger);
  font-size: var(--font-size-s);
}

.timezone-help-icon {
  margin-top: 2px;
}
</style>
